import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { getLocaleHash } from '@shared/utils/app-version';
import { languageEventEmitter } from '@shared/utils/event-bus/event-bus.util';
import { APUtilString } from '@shared/utils/string/string.util';

import type { TAstroPayI18nSetting, TLanguage, TSupportedLanguages } from './types';
import type { Locale } from 'date-fns/locale';
import type { i18n as I18nInstance } from 'i18next';

import { DEFAULT_DATE_FNS, LANGUAGES, LANGUAGES_DATE_FNS } from './constanst';

const defaultNS = 'common';

export class API18n {
  private static settings: TAstroPayI18nSetting;

  static appI18n: I18nInstance;

  static init = ({
    bucketUrl,
    defaultLanguage,
    reloadInterval,
    flagUrl,
    query = 'language',
    supportedLanguages = 'all',
    storageKey = '__ASTROPAY_LANGUAGE__',
  }: TAstroPayI18nSetting) => {
    this.settings = {
      bucketUrl,
      defaultLanguage,
      reloadInterval,
      flagUrl,
      query,
      supportedLanguages,
      storageKey,
    };

    const result = this.start().then((res) => {
      return (API18n.appI18n = res);
    });

    return result;
  };

  private static async start() {
    const lng = API18n.getQueryLanguage(this.settings.query) || undefined;

    await i18n
      .use(initReactI18next)
      .use(LanguageDetector)
      .use(HttpBackend)
      .init(
        {
          lng,
          fallbackLng: API18n.settings.defaultLanguage,
          load: 'languageOnly',
          ns: [defaultNS],
          defaultNS,
          detection: {
            lookupLocalStorage: this.settings.storageKey,
          },
          backend: {
            crossDomain: true,
            reloadInterval: API18n.settings.reloadInterval
              ? Number(API18n.settings.reloadInterval)
              : false,
            loadPath: async function (lngs: string[], i18nNamespaces: string[]) {
              const language = lngs.length > 0 ? lngs[0] : API18n.settings.defaultLanguage;
              const ns = i18nNamespaces.length > 0 ? i18nNamespaces[0] : defaultNS;
              // A content hash when the build recorded one, so the URL changes exactly when the file
              // does and the response can be cached forever (release/index.ts). Local dev has no
              // hashes and keeps the old cache-buster.
              const hash = getLocaleHash(language, ns);
              const query = hash ? `v=${hash}` : `t=${new Date().getTime()}`;
              return `${APUtilString.trimEnd(
                API18n.settings.bucketUrl,
                '/'
              )}/${language}/${ns}.json?${query}`;
            },
            parse: function (data: string) {
              return JSON.parse(data);
            },
          },
          interpolation: {
            escapeValue: false,
          },
        },
        (err) => {
          if (err) {
            console.error(
              `astropay-i18n-init-language : language not found : (loaded default [${this.settings.defaultLanguage}])`,
              err
            );
          }
        }
      );
    return i18n;
  }

  static getCurrent = (): TLanguage => {
    const foundLang = this.find(i18n.language);
    return foundLang;
  };

  static setLanguage = (language: TLanguage) => {
    const previousLanguage = i18n.language;
    const foundLang = this.find(language.lang.code);

    i18n.changeLanguage(foundLang.lang.code);

    // Emit language change event
    if (previousLanguage !== foundLang.lang.code) {
      languageEventEmitter.emit({
        from: previousLanguage,
        to: foundLang.lang.code,
        source: 'API18n.setLanguage',
      });
    }
  };

  static setLanguageByCode = (languageCode: string) => {
    const previousLanguage = i18n.language;

    i18n.changeLanguage(languageCode);

    // Emit language change event
    if (previousLanguage !== languageCode) {
      languageEventEmitter.emit({
        from: previousLanguage,
        to: languageCode,
        source: 'API18n.setLanguageByCode',
      });
    }
  };

  static getLanguages = (): TLanguage[] => {
    return API18n.getSupportedLanguages()
      .map((lang) => {
        const result: TLanguage = {
          lang: this.normalizeIso(lang._iso),
          name: lang.name,
          flag: lang.flag,
          file: lang.file,
          order: lang.order,
        };
        return result;
      })
      .sort((a, b) => (a.order > b.order ? 1 : -1));
  };

  static getLanguageName = (isoLanguage: string): string => {
    return (
      this.getLanguages().find((language) => language.lang.iso === isoLanguage)?.name || isoLanguage
    );
  };

  private static find = (lang: string) => {
    let inputLang = this.normalizeIso(lang);
    let filterA = this.getLanguages().filter((item) => {
      return item.lang.iso.toLowerCase() === inputLang.iso.toLowerCase();
    });

    if (!filterA || (filterA && filterA.length === 0)) {
      inputLang = this.normalizeIso(this.settings.defaultLanguage);
      filterA = this.getLanguages().filter((item) => {
        return item.lang.iso.toLowerCase() === inputLang.iso.toLowerCase();
      });
    }

    if (inputLang.isoCountry === null) {
      return filterA[0];
    }

    const filterB = filterA.filter((item) => {
      if (item.lang.isoCountry === null || inputLang.isoCountry === null) {
        return true;
      } else {
        return item.lang.isoCountry.toLowerCase() === inputLang.isoCountry.toLowerCase();
      }
    });

    if (filterB.length === 0) {
      return filterA[0];
    } else if (filterB.length === 1) {
      return filterB[0];
    } else {
      return filterB[0];
    }
  };

  private static getQueryLanguage = (key?: string): string | null => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams) {
        const lang = searchParams.get(key || 'language');
        if (lang) {
          const findedLang = this.find(lang);
          if (findedLang) {
            return findedLang.lang.code;
          }
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  private static normalizeIso = (_lang?: string) => {
    let lang = _lang;
    if (lang === undefined || lang === null || lang === '') {
      lang = this.settings.defaultLanguage;
    }

    let normalizedInput = lang.replace('_', '.').replace('-', '.');
    normalizedInput = normalizedInput.toLowerCase();
    const parts = normalizedInput.split('.');
    // Crear el objeto de salida
    const output = {
      iso: parts[0], //en
      isoCountry: parts[1] || null, //US
      code: parts[1] ? `${parts[0]}-${parts[1]}` : parts[0], //en_US | en
    };
    return output;
  };

  private static getSupportedLanguages = () => {
    if (this.settings.supportedLanguages === 'all') {
      return LANGUAGES;
    } else {
      const slang = this.settings.supportedLanguages as Array<TSupportedLanguages>;
      return LANGUAGES.filter((lang) => {
        return slang.includes(lang._iso as TSupportedLanguages);
      });
    }
  };

  static getDateFnsLanguage = (): Locale => {
    const langKey = this.getCurrent().order;
    return (
      LANGUAGES_DATE_FNS.find((dateLang) => dateLang.order === langKey)?.file || DEFAULT_DATE_FNS
    );
  };
}
